import { useContext, useState } from 'react';

import { ApplicationContext } from '../../../../../../shared/source/context/context';
import { useTablePagination } from '../../../common/data-grids/common/use-table-pagination';
import { SkillsDataGrid } from '../../../common/data-grids/skills/SkillsDataGrid';
import { useSkillsColumns } from '../../../common/data-grids/skills/use-skills-columns';
import { defaultPagination } from '../../../global-search/use-global-search-table-params';
import { useGlobalSearchRelatedSkillsQuery } from '../../../query-hooks/global-search/skills';

import type { Translate } from '../../../../../../shared/source/internationalization/translation';
import type { GlobalSearchSkill } from '../../../../data-contract/global-search';
import type { HeadlineRow, SkillRow, TableRow } from '../../../common/data-grids/skills/SkillsDataGrid';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import type { FunctionComponent } from 'react';

const generateRows = (
    translate: Translate,
    exactSkills: readonly GlobalSearchSkill[],
    relatedSkills: readonly GlobalSearchSkill[],
): readonly TableRow[] => {
    const exactRowsHeadline: HeadlineRow = {
        area: 'exact',
        title: translate('globalsearch.exact-skills'),
        type: 'headline',
    };

    const exactRows: readonly SkillRow[] = exactSkills.map((skill) => ({
        area: 'exact',
        skill,
        type: 'skill',
    }));

    const relatedRowsHeadline: HeadlineRow = {
        area: 'related',
        title: translate('globalsearch.related-skills'),
        type: 'headline',
    };

    const relatedRows: readonly SkillRow[] = relatedSkills.map((skill) => ({
        area: 'related',
        skill,
        type: 'skill',
    }));

    return [exactRowsHeadline, ...exactRows, relatedRowsHeadline, ...relatedRows];
};

export const SkillIdsTable: FunctionComponent<{ readonly skillIds: ReadonlyArray<string> }> = ({ skillIds }) => {
    const { translate } = useContext(ApplicationContext);

    const { paginationModel, setPaginationModel } = useTablePagination();

    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const { data, isFetching, isPending } = useGlobalSearchRelatedSkillsQuery({
        pagination: {
            page: paginationModel.page,
            size: paginationModel.pageSize,
        },
        skillIds,
        sortedFields: sortModel.map((item) => ({
            direction: item.sort ?? 'asc',
            field: item.field,
        })),
    });

    const columns = useSkillsColumns({ sortable: true });

    const skillData = data?.skillData ?? [];
    const pagination = data?.relatedSkillData.pagination ?? defaultPagination;
    const relatedSkills = data?.relatedSkillData.data ?? [];

    const finalRows = generateRows(translate, skillData, relatedSkills);
    const noDataMessage = translate('pages.global-search.no-data.skills');

    return (
        <SkillsDataGrid
            columns={columns}
            isFetching={isFetching}
            isPending={isPending}
            noDataMessage={noDataMessage}
            paginationModel={paginationModel}
            rowCount={pagination.count}
            rows={finalRows}
            sortModel={sortModel}
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setSortModel}
        />
    );
};
